import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputField } from 'src/components/atoms/input-field';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { useEditBookingRulesName } from 'src/features/booking-rules/api/hooks/use-edit-booking-rules-name';
import { z } from 'zod';

interface Props extends DialogActions {
  token: string;
  name: string;
}

const MAX_NAME_LENGTH = 255;

export const EditBookingRulesNameDialog = ({
  open,
  onCancel,
  token,
  name,
}: Props) => {
  const { t } = useTranslation(['bookingRules', 'validation']);

  const validationSchema = z.object({
    name: z
      .string()
      .min(1, t('validation:required'))
      .max(
        MAX_NAME_LENGTH,
        t('validation:maxCharacters', { count: MAX_NAME_LENGTH }),
      ),
  });

  type FormValues = z.infer<typeof validationSchema>;

  const defaultValues: FormValues = {
    name: '',
  };

  const { control, handleSubmit, reset } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    mode: 'onBlur',
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset({
        name,
      });
    }
  }, [open]);

  const handleOnCancel = () => {
    onCancel?.();
    reset(defaultValues);
  };

  const { mutate: editBookingRulesName, isPending: isEditingBookingRulesName } =
    useEditBookingRulesName(handleOnCancel);

  const handleEditBookingRulesName = (data: FormValues) => {
    editBookingRulesName({ token, name: data.name });
  };

  return (
    <Dialog
      open={open}
      title={t('bookingRules:common.editBookingRulesNameDialog.title')}
      onCancel={onCancel}
      onConfirm={handleSubmit(handleEditBookingRulesName)}
      isConfirmButtonSubmitting={isEditingBookingRulesName}>
      <Controller
        control={control}
        name="name"
        render={({
          field: { name, value, onChange, onBlur },
          fieldState: { error, invalid },
        }) => (
          <InputField
            name={name}
            label={t('bookingRules:common.editBookingRulesNameDialog.name')}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            error={invalid}
            helperText={error?.message}
            required
            fullWidth
          />
        )}
      />
    </Dialog>
  );
};
